// The web app (app.seed-bed.com): a public feed anyone can play, seed pages,
// profiles, and — signed in — your own seeds and the social layer.
//
// Its own package, its own build and its own worker, deployed
// once per channel (wrangler.jsonc) — so the channel is whichever
// deployment you opened, and the page only reports it. Reads and writes go to
// the core API through the worker (`webApi`, worker/forward.ts); the full
// player screen and the Audiotool export read the account worker's own
// routes (`api()`, `objectUrl()`), which stream audio same-origin.

import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AudiotoolExportPage } from "@web-shared/pages/AudiotoolExport";
import { AudiotoolProvider } from "@web-shared/hooks/useAudiotool";
import { clearSession, getSession, needsRefresh, refreshSession, resetPassword } from "@web-shared/lib/account";
import { track, getEntrySource, identify } from "@web-shared/lib/analytics";
import { Button } from "@web-shared/components/ui/button";
import { Input } from "@web-shared/components/ui/input";
import { seedPath } from "@/lib/ctas";
import { signInReturnPath } from "@/lib/return-path";
import { AuthForm, AuthGateProvider } from "./auth";
import { AmbientGlow } from "@/components/AmbientGlow";
import { useSession } from "./session";
import { PlayerProvider } from "./player";
import { BottomChromeProvider } from "./chrome";
import { Shell } from "./Shell";
import { FeedPage } from "./pages/FeedPage";
import { HomePage } from "./pages/HomePage";
import { UploadFilesPage } from "./pages/UploadFilesPage";
import { LibraryPage } from "./pages/LibraryPage";
import { SeedPage } from "./pages/SeedPage";
import { handleFromParam, ProfilePage } from "./pages/ProfilePage";
import { SearchPage } from "./pages/SearchPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { ChartsPage } from "./pages/ChartsPage";
import { AudiotoolHubPage } from "./pages/AudiotoolHub";
import { AudiotoolImportPage } from "./pages/AudiotoolImport";
import { JamPage } from "./pages/JamPage";
import { EventPage } from "./pages/EventPage";
import { EventAdminPage } from "./pages/EventAdminPage";
import { EventsPage } from "./pages/EventsPage";
import { EventsAdminPage } from "./pages/EventsAdminPage";
import { BlankRoomPage, ConnectedRoomPage, LocalRoomPage, NewRoomRedirect } from "./pages/RoomPage";

export function AccountApp() {
  const queryClient = useQueryClient();
  const session = useSession();
  // A stored session may be a day old: trade it for a fresh one before any
  // page bakes the access token into an audio URL.
  const [ready, setReady] = useState(() => {
    const s = getSession();
    return !s || !needsRefresh(s);
  });

  useEffect(() => {
    if (ready) return;
    void refreshSession().then(() => setReady(true));
  }, [ready]);

  // Keep the access token ahead of its expiry while the page stays open.
  useEffect(() => {
    if (!session) return;
    const due = Date.parse(session.accessExpiresAt) - Date.now() - 2 * 60_000;
    const timer = window.setTimeout(() => void refreshSession(), Math.max(due, 10_000));
    return () => window.clearTimeout(timer);
  }, [session]);

  // Who is signed in changes every per-viewer answer (likedByMe, the
  // Following tab, what opens): drop the cache whenever it does. Reset, not
  // remove: removing also cancels the fetch a page on screen has just started
  // for the new person, and it sits on its skeleton forever (signing up from
  // the dialog on an event page). Reset clears the answers and refetches
  // what's on screen. Skipped on first render — nothing is stale yet.
  const userId = session?.user.id ?? "";
  const seenUser = useRef(userId);
  useEffect(() => {
    if (seenUser.current === userId) return;
    seenUser.current = userId;
    void queryClient.resetQueries({ queryKey: ["web"] });
  }, [userId, queryClient]);

  const signOut = useCallback(() => {
    clearSession();
    queryClient.clear();
  }, [queryClient]);

  // --- Analytics: page_view on every navigation + entry source on first load ---
  const location = useLocation();
  useEffect(() => {
    // Eagerly resolve entry source on mount so it captures the first URL.
    getEntrySource();
  }, []);
  useEffect(() => {
    const seedMatch = location.pathname.match(/^\/s\/([^/]+)$/);
    track("page_view", {
      detail: location.pathname,
      ...(seedMatch ? { seedId: seedMatch[1] } : {}),
    });
  }, [location.pathname]);

  const beta = useChannel() === "beta";

  if (!ready) return <div className="min-h-dvh" />;

  const framed = (page: ReactNode, wide = false) => (
    <Shell beta={beta} signOut={signOut} wide={wide}>
      {page}
    </Shell>
  );

  return (
    <AuthGateProvider beta={beta}>
      <BottomChromeProvider>
      <PlayerProvider>
        <WithAudiotool>
        <Routes>
          {/* Home: signed out, this week's Flip with the first-visit questions
              over it; signed in, the feed. */}
          <Route path="/" element={<HomeRoute framed={framed} />} />
          {/* The feed for anyone — where Discover goes when / is the questions. */}
          <Route path="/discover" element={framed(<FeedPage />, true)} />
          <Route path="/search" element={framed(<SearchPage />)} />
          <Route path="/charts" element={framed(<ChartsPage />, true)} />
          <Route path="/jam" element={framed(<JamPage />, true)} />
          {/* "Pick a seed": the feed, each card offering Start Jam. */}
          <Route path="/jam/start" element={framed(<FeedPage cta="jam" />, true)} />
          {/* It was /multiplayer until the page took the name people already used. */}
          <Route path="/multiplayer" element={<Navigate to="/jam" replace />} />
          <Route path="/s/:id" element={framed(<SeedPage />, true)} />
          {/* The seed page grew the player into itself (press play and the hero
              becomes it), so this address is only ever an old link now — and it
              needed an account to stream, which a shared link does not have. */}
          <Route path="/s/:id/player" element={<LegacySeedLink />} />
          <Route path="/export/audiotool/:id" element={<AudiotoolExportPage />} />
          {/* A seed's Audiotool projects, and bringing one back as a seed. */}
          <Route path="/s/:id/audiotool" element={framed(<AudiotoolHubPage />)} />
          <Route
            path="/upload/audiotool"
            element={framed(<SignedIn why="Sign in to plant a seed"><AudiotoolImportPage /></SignedIn>)}
          />
          {/* A folder or zip of audio from any DAW. An account first, so
              nobody fills in the whole upload to be stopped at the end. */}
          <Route path="/upload/files" element={framed(<SignedIn why="Sign in to plant a seed"><UploadFilesPage /></SignedIn>)} />
          {/* The producer's own clips, MIDI and folders, and the clips of
              every seed they touched — organised without a project open. */}
          <Route path="/library" element={framed(<SignedIn why="Sign in to see your library"><LibraryPage /></SignedIn>, true)} />
          {/* Browser remix: local solo editing, no auth required. */}
          <Route path="/room/local/:seedId" element={<LocalRoomPage />} />
          {/* A seed made from nothing: a fresh draft, then the draft itself. */}
          <Route path="/room/new" element={<NewRoomRedirect />} />
          <Route path="/room/draft/:draftId" element={<BlankRoomPage />} />
          {/* Connected room: multiplayer jam editing, signed in. */}
          <Route path="/room/:jamId" element={<RoomRoute framed={framed} />} />
          {/* Listening in on a live jam: the room's own player, observer only. */}
          <Route path="/jams/:jamId" element={<RoomRoute framed={framed} listen />} />
          {/* Secret Sample and later events: join, pool a seed, get one to flip. */}
          <Route path="/events" element={framed(<EventsPage />, true)} />
          <Route
            path="/events/admins"
            element={framed(<SignedIn why="Sign in to manage events"><EventsAdminPage /></SignedIn>, true)}
          />
          <Route path="/events/:slug" element={framed(<EventPage />)} />
          <Route
            path="/events/:slug/admin"
            element={framed(<SignedIn why="Sign in to run this event"><EventAdminPage /></SignedIn>, true)}
          />
          <Route path="/notifications" element={framed(<SignedIn why="Sign in to see your notifications"><NotificationsPage /></SignedIn>)} />
          <Route path="/signin" element={<AuthPage mode="signin" beta={beta} />} />
          {/* Where a reset email's link lands. */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/signup" element={<AuthPage mode="signup" beta={beta} />} />
          {/* Links from before the web app had public seed pages. */}
          <Route path="/seeds/:id" element={<LegacySeedLink />} />
          {/* `/@handle`. Last among single segments, so every named page wins. */}
          <Route path="/:at" element={<ProfileRoute framed={framed} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </WithAudiotool>
      </PlayerProvider>
      </BottomChromeProvider>
    </AuthGateProvider>
  );
}

/**
 * A live jam's room: editing (/room/:jamId, signed in), or listening in
 * (/jams/:jamId, anyone — the same editor with nothing that writes; the API
 * opens a jam off a non-private seed to a signed-out observer and nothing
 * more). Both addresses render this one component, so Join (which swaps the
 * one for the other, through the sign-in gate when signed out) keeps the
 * page, and the audio it has loaded, rather than remounting it.
 */
function RoomRoute({ framed, listen = false }: { framed: (page: ReactNode, wide?: boolean) => ReactNode; listen?: boolean }) {
  const session = useSession();
  if (session || listen) return <ConnectedRoomPage listen={listen} />;
  return <>{framed(<SignedIn why="Sign in to join this jam"><></></SignedIn>)}</>;
}

function ProfileRoute({ framed }: { framed: (page: ReactNode, wide?: boolean) => ReactNode }) {
  const { at } = useParams();
  if (!handleFromParam(at)) return <Navigate to="/" replace />;
  return <>{framed(<ProfilePage />, true)}</>;
}

/**
 * The Audiotool SDK (from main, #42) only where it is needed: on the export,
 * upload and seed-Audiotool pages, and on the page Audiotool's sign-in
 * redirects back to — the bare origin, carrying `code` and `state` — where it
 * finishes the sign-in and returns to where it left. Everywhere else the SDK
 * is never loaded.
 */
function WithAudiotool({ children }: { children: ReactNode }) {
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const needed =
    location.pathname.startsWith("/export/audiotool/") ||
    location.pathname === "/upload/audiotool" ||
    /^\/s\/[^/]+\/audiotool$/.test(location.pathname) ||
    (search.has("code") && search.has("state"));
  return needed ? <AudiotoolProvider>{children}</AudiotoolProvider> : <>{children}</>;
}

/**
 * /: the first-visit questions over this week's Flip for anyone who arrives
 * signed out; the feed for anyone signed in. Decided when the page mounts, so
 * signing in partway through the questions (Plant asks for an account) keeps
 * you on the step you were on rather than swapping the page for the feed.
 */
function HomeRoute({ framed }: { framed: (page: ReactNode, wide?: boolean) => ReactNode }) {
  const session = useSession();
  const [guest] = useState(() => session === null);
  if (guest) return <HomePage />;
  return <>{framed(<FeedPage />, true)}</>;
}

function LegacySeedLink() {
  const { id = "" } = useParams();
  return <Navigate to={seedPath(id)} replace />;
}

/**
 * Renders `children` for a signed-in person; otherwise the sign-in form, right
 * here, on the home page's light — the page you came for opens in its place
 * once you're in, so a link to a room is still a link to the room.
 */
function SignedIn({ why, children }: { why: string; children: ReactNode }) {
  const session = useSession();
  const beta = useChannel() === "beta";
  if (session) return <>{children}</>;
  return (
    <>
      <AmbientGlow />
      <div className="relative z-10 mx-auto flex min-h-[60dvh] w-full max-w-sm flex-col justify-center py-10">
        <p className="mb-4 text-center text-lg font-bold tracking-tight">{why}</p>
        <div className="rounded-2xl border border-hairline bg-raised p-6">
          <AuthForm beta={beta} onDone={() => {}} />
        </div>
      </div>
    </>
  );
}

/** /reset-password?token=…: a new password, then signed in and home. */
function ResetPasswordPage() {
  const navigate = useNavigate();
  const token = new URLSearchParams(useLocation().search).get("token") ?? "";
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(token ? null : "This link is missing its token. Ask for a new one from Sign in.");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8 || !token) return;
    setBusy(true);
    setError(null);
    try {
      await resetPassword(token, password);
      identify();
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not set the password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-dvh items-center justify-center p-6">
      <AmbientGlow />
      <form onSubmit={submit} className="relative z-10 w-full max-w-sm space-y-4 rounded-2xl border border-hairline bg-raised p-6">
        <div className="flex items-center gap-2">
          <img src="/seedbed-logo.webp" alt="" className="size-5" />
          <h1 className="text-sm font-semibold">Set a new password</h1>
        </div>
        <Input
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="New password (8+ characters)"
          aria-label="New password"
          autoFocus
        />
        {error && <p className="text-xs text-removed">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy || password.length < 8 || !token}>
          {busy ? "Saving…" : "Save and sign in"}
        </Button>
        <p className="text-center text-xs text-muted-foreground">You'll be signed out everywhere else.</p>
      </form>
    </div>
  );
}

function AuthPage({ mode, beta }: { mode: "signin" | "signup"; beta: boolean }) {
  const navigate = useNavigate();
  const session = useSession();
  const search = useLocation().search;
  const back = signInReturnPath(search);
  const forgot = new URLSearchParams(search).get("forgot") === "1";
  if (session) return <Navigate to={back} replace />;
  return (
    <div className="relative flex min-h-dvh items-center justify-center p-6">
      <AmbientGlow />
      <div className="relative z-10 w-full max-w-sm rounded-2xl border border-hairline bg-raised p-6">
        <AuthForm initialMode={mode} initialForgot={forgot} beta={beta} onDone={() => navigate(back, { replace: true })} />
      </div>
    </div>
  );
}

/** Which channel this deployment serves — asked of the worker, not built in,
 * since one build ships to both. Unknown until it answers, and harmless then. */
function useChannel(): "stable" | "beta" | null {
  const info = useQuery({
    queryKey: ["account", "info"],
    queryFn: async () => {
      const res = await fetch("/api/account/info");
      return res.ok ? ((await res.json()) as { channel: "stable" | "beta" }) : null;
    },
    staleTime: Infinity,
  });
  return info.data?.channel ?? null;
}
